import { ChangeEvent, FocusEvent } from 'react';
import { useSoftKeyboard } from '../contexts/SoftKeyboardContext';

type KeyboardType = 'numeric' | 'alphanumeric' | 'alphabetic';

type InputElement = HTMLInputElement | HTMLTextAreaElement;

interface Options {
  id: string;
  type: KeyboardType;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  manualValidator?: (value: string) => boolean;
  maskValue?: boolean;
  /** Use the device/browser keyboard even when the on-screen soft keyboard is enabled (e.g. report pages). */
  preferNativeKeyboard?: boolean;
}

export const useSoftKeyboardField = ({
  id,
  type,
  label,
  value,
  onChange,
  manualValidator,
  maskValue,
  preferNativeKeyboard = false,
}: Options) => {
  const { enabled, openKeyboard } = useSoftKeyboard();
  const useOnScreenKeyboard = enabled && !preferNativeKeyboard;

  const handleFocus = (event: FocusEvent<InputElement>) => {
    if (preferNativeKeyboard || !enabled || event.target.hasAttribute('disabled')) return;
    openKeyboard({ id, type, label, value, onChange, maskValue });
  };

  const handleChange = (event: ChangeEvent<InputElement>) => {
    if (useOnScreenKeyboard) return;
    const next = event.target.value;
    if (manualValidator && !manualValidator(next)) return;
    onChange(next);
  };

  return {
    readOnly: useOnScreenKeyboard,
    onFocus: handleFocus,
    onChange: handleChange,
  };
};
